import type { Asset } from "@shared/types/asset";
import AppHeader from "@shared/ui/AppHeader";
import AssetCard from "@shared/ui/AssetCard";
import AssetCardMobile from "@shared/ui/AssetCardMobile";
import Breadcrumbs from "@shared/ui/Breadcrumbs";
import QRCodeModal from "@shared/ui/QRCodeModal";
import React from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import PAID_FEATURES from "$/settings/paid-features.json";
import AppHeaderWithSidebarOption from "~/components/AppHeaderWithSidebarOption";
import MessageSpinner from "~/components/chat/_messageSpinner";
import { useAuth } from "~/components/general";
import MachineListItem from "~/components/machines/MachineListItem";
import { SHARED_ASSETS_VIEW_QR } from "~/constants";
import SingleSharedAssetTabs from "~/containers/Assets/Shared/SingleSharedAssetTabs";
import Create from "~/containers/Requests/Create";
import { useGetAssetWithOrgById } from "~/services/asset";
import { UserType } from "~/types/user";
import { getEnums } from "~/utils";
import { isLightColor, generateBrandingButtonStyle } from "~/utils/_colors";
import { registerMixpanelEvent } from "~/utils/_mixpanel";
import { useGetAppConfigValue } from "~/utils/appFeatures";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";

const paidFeatures = getEnums(PAID_FEATURES, "reference");

const SingleSharedAsset = ({
  asset,
  assetParts,
  isQRCodeFlow,
  hideHeader = false,
  headerSlot,
  onAssetClick,
  onRequestClick,
  onAiChatClick,
  showCreateRequest = true,
  createRequestSlot,
  connectionId: connectionIdProp,
}: {
  asset: Asset;
  assetParts;
  isQRCodeFlow: boolean;
  /** Hide the default header (breadcrumbs + sidebar). Useful when embedding in another layout. */
  hideHeader?: boolean;
  /** Custom header element to render instead of the default. Only used when hideHeader is false. */
  headerSlot?: React.ReactNode;
  /** Optional callback for sub-asset click. When provided, replaces default Link navigation. */
  onAssetClick?: (assetId: string, assetName: string) => void;
  /** Optional callback for request click. When provided, replaces default Link navigation. */
  onRequestClick?: (
    requestId: string,
    requestTitle: string,
    isSupportTicket?: boolean,
  ) => void;
  /** Optional callback for AI chat click. When provided, replaces default navigation. */
  onAiChatClick?: (assistantId: string, assistantName: string) => void;
  showCreateRequest?: boolean;
  /** Custom CTA rendered inside the asset card, next to the QR code button. */
  createRequestSlot?: React.ReactNode;
  /**
   * The portal this asset is being viewed in. Decides which sharing grants
   * apply — a reshared asset resolves differently in a parent portal and in its
   * child. Defaults to the `connectionId` route param, which the customer
   * portal's route does not have (it names the same thing `id`), so that app
   * passes it explicitly.
   */
  connectionId?: string;
}) => {
  const { messages } = useIntl();

  const { connectionId: connectionIdParam, assetId } = useParams() as {
    connectionId: string;
    assetId: string;
  };
  const connectionId = connectionIdProp ?? connectionIdParam;

  const [showCreateDrawer, setShowCreateDrawer] = React.useState(false);

  // eslint-disable-next-line prefer-const
  let { loading: assetDataLoading, asset: assetData } = useGetAssetWithOrgById({
    id: assetId,
    connectionId,
    skipCondition: !!asset?._id,
  });

  const [showQRModal, setShowQRModal] = React.useState(null);

  assetData = assetData || asset;

  const assetAccess = assetData?.sharedAssetAccess;

  const sharedOrganization = assetData?.oem;

  const breadCrumbs = React.useMemo(
    () => [
      {
        label: messages["sharedAssets"]["title"],
        link: "/app/asset-hub/shared",
      },
      {
        label: sharedOrganization?.name,
        link: `/app/asset-hub/shared/${connectionId}`,
      },
      {
        label: assetData?.name,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sharedOrganization?.name, assetData?.name],
  );

  const isLightBackground = isLightColor(
    sharedOrganization?.backgroundColor || "",
  );

  const buttonStyle = React.useMemo(() => {
    const lightColorButtonStyle = generateBrandingButtonStyle(
      sharedOrganization?.backgroundColor,
    );

    return isLightBackground
      ? lightColorButtonStyle
      : { backgroundColor: sharedOrganization?.backgroundColor };
  }, [sharedOrganization?.backgroundColor, isLightBackground]);

  const { user } = useAuth() as { user: UserType };

  const appConfig = useGetAppConfigValue();
  const isAiAssistantPaid = isPaidFeatureAvailable(
    paidFeatures.aiAssistants,
    // @ts-ignore
    { oem: sharedOrganization },
    appConfig,
    false,
  );

  const assetTypeIdToLabelMap = React.useMemo(() => {
    const map = {};
    sharedOrganization?.assetTypes.forEach((type) => {
      map[type._id] = type.name;
    });
    return map;
  }, [sharedOrganization?.assetTypes, assetData?._id]);

  const isLoading =
    assetDataLoading || !assetData || !Object.keys(assetData).length;

  return (
    <div className="flex flex-col h-full">
      {headerSlot ? (
        headerSlot
      ) : !hideHeader ? (
        isQRCodeFlow ? (
          <AppHeader padding="p-2xl">
            <img
              src="https://assets.makula.dev/public/makula-logo-email.png"
              alt="company logo"
              className="max-h-3xl"
            />
          </AppHeader>
        ) : (
          <AppHeaderWithSidebarOption padding="p-2xl">
            <div className={isQRCodeFlow ? "pointer-events-none" : ""}>
              <Breadcrumbs options={breadCrumbs} />
            </div>
          </AppHeaderWithSidebarOption>
        )
      ) : null}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center flex-1">
          <MessageSpinner />
        </div>
      ) : (
        <>
          <div className="flex flex-col">
            <img
              src={
                (sharedOrganization?.coverPhoto || null) ??
                "/images/default/cover-photo.png"
              }
              className="object-cover"
              style={{ height: "116px" }}
            />
          </div>
          <div className="flex md:flex-row flex-col md:pl-2xl">
            <AssetCard
              asset={assetData}
              className="md:block hidden"
              buttonStyle={buttonStyle}
              isSharedPage
              showCreateRequest={!isQRCodeFlow && showCreateRequest}
              createRequestSlot={!isQRCodeFlow ? createRequestSlot : null}
              assetType={assetTypeIdToLabelMap[assetData?.assetType] || "--"}
              onClickCreateRequest={() => setShowCreateDrawer(true)}
              sharedAssistant={
                isAiAssistantPaid ? assetData.sharedAssistant : null
              }
              onAiChatClick={onAiChatClick}
              qrCodeInfo={
                !isQRCodeFlow &&
                assetAccess.qrCodes && (
                  <>
                    <MachineListItem.QRCode
                      widthClass="w-fit"
                      handleQRCodeClick={() => {
                        setShowQRModal(true);
                        registerMixpanelEvent(SHARED_ASSETS_VIEW_QR, {
                          sharerOrgUrl: assetData?.oem?.slug,
                        });
                      }}
                      newQRCodeIcon
                    />
                    <QRCodeModal
                      isOpen={!!showQRModal}
                      onClose={() => setShowQRModal(null)}
                      assetName={
                        (showQRModal?._id ? showQRModal : assetData)?.name ?? ""
                      }
                      serialNumber={
                        (showQRModal?._id ? showQRModal : assetData)
                          ?.serialNumber ?? ""
                      }
                      generalAccessUrl={
                        (showQRModal?._id ? showQRModal : assetData)
                          ?.generalAccessUrl ?? ""
                      }
                      sharedBy={sharedOrganization?.name}
                      sharedByLogo={sharedOrganization?.brandLogo}
                    />
                  </>
                )
              }
            />
            <AssetCardMobile
              asset={assetData}
              className="md:hidden flex"
              buttonStyle={buttonStyle}
              isSharedPage
              showCreateRequest={!isQRCodeFlow && showCreateRequest}
              createRequestSlot={!isQRCodeFlow ? createRequestSlot : null}
              assetType={assetTypeIdToLabelMap[assetData?.assetType] || "--"}
              onClickCreateRequest={() => setShowCreateDrawer(true)}
              sharedAssistant={
                user?.hasAiAssistantAccess && isAiAssistantPaid
                  ? assetData.sharedAssistant
                  : null
              }
              onAiChatClick={onAiChatClick}
              qrCodeInfo={
                !isQRCodeFlow &&
                assetAccess.qrCodes && (
                  <>
                    <MachineListItem.QRCode
                      widthClass="w-fit"
                      handleQRCodeClick={() => {
                        setShowQRModal(true);
                        registerMixpanelEvent(SHARED_ASSETS_VIEW_QR, {
                          sharerOrgUrl: assetData?.oem?.slug,
                        });
                      }}
                      newQRCodeIcon
                    />
                    <QRCodeModal
                      isOpen={!!showQRModal}
                      onClose={() => setShowQRModal(null)}
                      assetName={
                        (showQRModal?._id ? showQRModal : assetData)?.name ?? ""
                      }
                      serialNumber={
                        (showQRModal?._id ? showQRModal : assetData)
                          ?.serialNumber ?? ""
                      }
                      generalAccessUrl={
                        (showQRModal?._id ? showQRModal : assetData)
                          ?.generalAccessUrl ?? ""
                      }
                      sharedBy={sharedOrganization?.name}
                      sharedByLogo={sharedOrganization?.brandLogo}
                    />
                  </>
                )
              }
            />
            <SingleSharedAssetTabs
              asset={assetData}
              assetParts={assetParts}
              connectionId={connectionId}
              isQRCodeFlow={isQRCodeFlow}
              setShowQRModal={setShowQRModal}
              onAssetClick={onAssetClick}
              onRequestClick={onRequestClick}
            />
          </div>
          {showCreateDrawer && !isQRCodeFlow && (
            <Create
              onClose={() => setShowCreateDrawer(false)}
              selectedAsset={assetData}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SingleSharedAsset;
